/**
 * Task 151-B — per-service "advanced terms" editor inside the package
 * drawer. Keeps the V2 commercial columns editable (so the reference UX
 * does not lose existing functionality): pricing model, retainer,
 * spend %, cadence, quantity, deliverables, terms notes, and sort order.
 * (Task 280I — the `included` toggle moved to the PackageDrawer MAIN flow
 * so the in-scope state is always visible; it is no longer rendered here.)
 *
 * For `ads`-kind lines the pricing model / retainer / spend% are DERIVED
 * from the config ads pricing (shown read-only here) so the two sources
 * never diverge — see `deriveLegacyPricingFromConfig`.
 *
 * Task 166B — the destructive `تصنيف تقني` / kind dropdown was removed
 * from the UI entirely. The agency should not mutate a saved service's
 * kind between social ↔ ads ↔ influencer ↔ content ↔ brand; if they
 * need another type, they add a separate service line. `kind` still
 * round-trips through `config.kind` via `buildSetServicesInput` →
 * `serializeConfig(l.kind, ...)`; the model is untouched.
 */

import type { PricingModel, ServiceCadence } from '../../api/types';
import { PRICING_MODELS, SERVICE_CADENCES } from './constants';
import { SimpleField, SimpleSelect } from './formPrimitives';
import { deriveLegacyPricingFromConfig, type DrawerLineState } from './packageDrawerModel';
import { familyLayout, type PackageServiceFamily } from './packageServiceFamily';
import { ServiceConfigSection } from './serviceConfigSections';
import { NumberField } from './serviceConfigSections/primitives';

function pricingModelLabel(value: PricingModel | null): string {
  const found = PRICING_MODELS.find((o) => o.value === (value ?? ''));
  return found ? found.label : '—';
}

function parseNullableNumber(raw: string): number | null {
  if (raw.trim().length === 0) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function AdvancedTermsSection({
  line,
  family,
  disabled,
  onChange,
}: {
  readonly line: DrawerLineState;
  /** Task 161 — the sellable family (drives where the typed config renders). */
  readonly family: PackageServiceFamily;
  readonly disabled?: boolean;
  readonly onChange: (partial: Partial<DrawerLineState>) => void;
}) {
  const isAds = line.kind === 'ads';
  const derived = deriveLegacyPricingFromConfig(line.kind, line.config);
  // Task 161B — drive the advanced ServiceConfigSection visibility from the
  // per-family layout descriptor. Typed-config families render the typed
  // sub-config in the MAIN flow (so the advanced copy would be redundant);
  // the influencer family deliberately hides count / category (the "fake
  // workflow fields" the spec rules out); only content_production / custom
  // keep the typed sub-config visible here.
  const showConfigHere = familyLayout(family).showTypedConfigInAdvanced;
  // Task 166 + 166B — for the two typed families (social_media + paid_campaigns)
  // the main flow already covers every business-meaningful field. The advanced
  // section collapses to a compact "internal settings" block (notes +
  // terms + sort_order) plus the ads-only read-only derived pricing
  // display when relevant. The editable pricing_model / retainer / spend%
  // block AND the quantity / cadence / currency grid are NOT rendered for
  // these families — but the underlying line state is preserved verbatim by
  // `buildSetServicesInput` so saved data round-trips unchanged. The kind
  // override dropdown was removed entirely in Task 166B — `kind` is model-
  // owned and read-only from the UI.
  const isCompactInternalFamily = family === 'social_media' || family === 'paid_campaigns';

  return (
    <details>
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--ink-2)',
          padding: '4px 0',
        }}
      >
        {isCompactInternalFamily ? 'إعدادات داخلية' : 'إعدادات متقدّمة'}
      </summary>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
        {/* Task 166B — the destructive `تصنيف تقني` / kind override dropdown
            was removed from the UI entirely. The agency adds a separate
            service line when they need another type; mutating a saved
            service's kind between social ↔ ads ↔ influencer ↔ content ↔
            brand is product-wrong. `kind` still round-trips via
            `buildSetServicesInput` → `serializeConfig(l.kind, ...)`; the
            underlying model is untouched. */}

        {/* Task 161B — only families whose layout descriptor says so render the
            typed sub-config here; for typed-config and influencer-scope families
            the main flow already covers it (or intentionally simplifies it). */}
        {showConfigHere ? (
          <ServiceConfigSection
            kind={line.kind}
            config={line.config}
            disabled={disabled}
            onChange={(raw) => onChange({ config: raw })}
          />
        ) : null}

        {/* Task 280I — the "مضمّنة في نطاق الباقة" toggle was moved to the MAIN
            service flow (PackageDrawer) so the in-scope state is visible without
            expanding this section. It reads/writes the same `line.included`
            state; nothing else here changed. */}

        {/* Task 166 — ads-only read-only derived pricing display stays even in
            the compact-internal layout because it is informational (no fields
            to edit). The editable pricing_model / retainer / spend% block is
            REMOVED for the typed families per the user spec — those values
            are preserved in state and round-trip unchanged via
            `buildSetServicesInput`. */}
        {isAds && derived ? (
          <div
            style={{
              padding: '10px 12px',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)',
              background: 'var(--cream-2)',
            }}
          >
            <div className="lbl" style={{ fontSize: 11, marginBottom: 4 }}>
              نموذج التسعير (يُشتق من إعدادات تسعير الإعلانات أعلاه)
            </div>
            <div className="tiny" style={{ color: 'var(--ink-2)' }}>
              {pricingModelLabel(derived.pricing_model)}
              {derived.retainer_amount !== null ? ` · ثابت: ${derived.retainer_amount}` : ''}
              {derived.spend_percentage !== null ? ` · نسبة: ${derived.spend_percentage}%` : ''}
            </div>
          </div>
        ) : !isCompactInternalFamily ? (
          <>
            <SimpleSelect
              label="نموذج التسعير"
              value={line.pricing_model ?? ''}
              options={PRICING_MODELS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => onChange({ pricing_model: v === '' ? null : (v as PricingModel) })}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <SimpleField
                label={`السعر الثابت (${line.retainer_currency})`}
                value={line.retainer_amount !== null ? String(line.retainer_amount) : ''}
                type="number"
                dirLtr
                placeholder="—"
                onChange={(v) => onChange({ retainer_amount: parseNullableNumber(v) })}
              />
              <SimpleField
                label="نسبة من الإنفاق (%)"
                value={line.spend_percentage !== null ? String(line.spend_percentage) : ''}
                type="number"
                dirLtr
                placeholder="—"
                onChange={(v) => onChange({ spend_percentage: parseNullableNumber(v) })}
              />
            </div>
          </>
        ) : null}

        {/* Task 166 — quantity / cadence / currency grid is content-production
            shaped UI; rendering it for typed families made advanced look
            like a duplicated/conflicting setup. Hidden for social_media +
            paid_campaigns; preserved in state via `buildSetServicesInput`. */}
        {!isCompactInternalFamily ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <SimpleField
              label="الكمية"
              value={line.quantity !== null ? String(line.quantity) : ''}
              type="number"
              dirLtr
              placeholder="—"
              onChange={(v) => onChange({ quantity: parseNullableNumber(v) })}
            />
            <SimpleSelect
              label="التكرار"
              value={line.cadence ?? ''}
              options={SERVICE_CADENCES.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => onChange({ cadence: v === '' ? null : (v as ServiceCadence) })}
            />
            <SimpleField
              label="عملة السعر"
              value={line.retainer_currency}
              dirLtr
              maxLength={3}
              placeholder="SAR"
              onChange={(v) => onChange({ retainer_currency: v.toUpperCase() })}
            />
          </div>
        ) : null}

        <SimpleField
          label="ملاحظة داخلية"
          value={line.notes ?? ''}
          textarea
          maxLength={1000}
          placeholder="ملاحظة داخلية لفريقك حول هذه الخدمة…"
          onChange={(v) => onChange({ notes: v.trim().length === 0 ? null : v })}
        />

        <SimpleField
          label="ملاحظات الشروط"
          value={line.terms_notes ?? ''}
          textarea
          maxLength={2000}
          placeholder="شروط أو تفاصيل تعاقدية إضافية لهذه الخدمة…"
          onChange={(v) => onChange({ terms_notes: v.trim().length === 0 ? null : v })}
        />

        <div style={{ maxWidth: 160 }}>
          <NumberField
            label="ترتيب العرض"
            value={line.sort_order}
            disabled={disabled}
            onChange={(v) => onChange({ sort_order: v })}
          />
        </div>
      </div>
    </details>
  );
}
